import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Button } from '@/components/atoms/Button';
import { Badge } from '@/components/atoms/Badge';
import { comma } from '@/lib/format';
import { toast } from '@/store/toastStore';

/**
 * 어드민 — 친구 추천 현황 (2026-05 개편: 보류/지급 탭)
 *  탭1 전체 / 탭2 지급 대기(pending) / 탭3 지급 완료(granted)
 *  지급 대기 탭은 자격 진단(가입경과/활동수/충족여부) 표시 + 수동 지급 버튼
 */

type Tab = 'all' | 'pending' | 'granted';

interface ReferralRow {
  id: number;
  referrer_id: string;
  referrer_nickname: string | null;
  referee_id: string;
  referee_nickname: string | null;
  referrer_points: number;
  referee_points: number;
  referrer_ip: string | null;
  status: string;
  created_at: string;
  granted_at: string | null;
}

interface PendingRow extends ReferralRow {
  referrer_is_deleted: boolean;
  referrer_is_inactive: boolean;
  referee_is_deleted: boolean;
  referee_is_inactive: boolean;
  referee_created_at: string;
  referee_days_elapsed: number;
  referee_post_count: number;
  referee_comment_count: number;
  qualified: boolean;
  pending_reason: string | null;
}

const LIMIT = 30;

export function ReferralsPage() {
  const [tab, setTab] = useState<Tab>('all');
  const [page, setPage] = useState(1);

  // 탭 전환 시 페이지 리셋
  const switchTab = (t: Tab) => {
    setTab(t);
    setPage(1);
  };

  return (
    <div className="w-full">
      <h1 className="mb-6 text-2xl font-extrabold">친구 추천 현황</h1>

      {/* 탭 */}
      <div className="mb-4 flex gap-2 border-b border-[var(--color-border)]">
        {([
          { key: 'all', label: '전체 추천' },
          { key: 'pending', label: '지급 대기' },
          { key: 'granted', label: '지급 완료' },
        ] as Array<{ key: Tab; label: string }>).map((t) => (
          <button
            key={t.key}
            onClick={() => switchTab(t.key)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
              tab === t.key
                ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                : 'border-transparent text-[var(--color-text-sub)] hover:text-[var(--color-text)]'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'pending' ? (
        <PendingTab page={page} setPage={setPage} />
      ) : (
        <ListTab tab={tab} page={page} setPage={setPage} />
      )}
    </div>
  );
}

/** 전체/완료 — 단일 테이블 */
function ListTab({
  tab,
  page,
  setPage,
}: {
  tab: Exclude<Tab, 'pending'>;
  page: number;
  setPage: (n: number) => void;
}) {
  const qs = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
  if (tab === 'granted') qs.set('status', 'granted');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'referrals', tab, page],
    queryFn: () =>
      get<{ total: number; rows: ReferralRow[]; page: number; limit: number }>(
        `/admin/referrals?${qs.toString()}`,
      ),
    staleTime: 30_000,
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;
  const totalReferrer = (data?.rows ?? []).reduce((s, r) => s + r.referrer_points, 0);
  const totalReferee = (data?.rows ?? []).reduce((s, r) => s + r.referee_points, 0);

  return (
    <>
      <div className="mb-4 grid grid-cols-3 gap-3">
        <SummaryCard label="총 건수" value={isLoading ? '-' : `${(data?.total ?? 0).toLocaleString()}건`} />
        <SummaryCard label="페이지 A 합계" value={isLoading ? '-' : `${comma(totalReferrer)}P`} />
        <SummaryCard label="페이지 B 합계" value={isLoading ? '-' : `${comma(totalReferee)}P`} />
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : (data?.rows ?? []).length === 0 ? (
        <EmptyMessage>
          {tab === 'granted' ? '지급 완료된 추천이 없어요.' : '추천 기록이 아직 없어요.'}
        </EmptyMessage>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
          <table className="min-w-full text-sm">
            <thead className="border-b border-[var(--color-border)] bg-[var(--color-bg)]/40 text-xs text-[var(--color-text-sub)]">
              <tr>
                <th className="px-3 py-2 text-left">신청일</th>
                <th className="px-3 py-2 text-left">상태</th>
                <th className="px-3 py-2 text-left">추천인 (A)</th>
                <th className="px-3 py-2 text-left">피추천인 (B)</th>
                <th className="px-3 py-2 text-right">A 포인트</th>
                <th className="px-3 py-2 text-right">B 포인트</th>
                <th className="px-3 py-2 text-left">지급일</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--color-border)]">
              {data!.rows.map((r) => (
                <tr key={r.id} className="hover:bg-[var(--color-bg)]/40">
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-[var(--color-text-sub)] tabular-nums">
                    {fmtDate(r.created_at)}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{r.referrer_nickname ?? '-'}</div>
                    <code className="text-[10px] text-[var(--color-text-sub)]">{r.referrer_id.slice(0, 8)}…</code>
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-semibold">{r.referee_nickname ?? '-'}</div>
                    <code className="text-[10px] text-[var(--color-text-sub)]">{r.referee_id.slice(0, 8)}…</code>
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-[var(--color-primary)]">
                    +{comma(r.referrer_points)}P
                  </td>
                  <td className="px-3 py-2 text-right font-semibold tabular-nums text-[var(--color-rise)]">
                    +{comma(r.referee_points)}P
                  </td>
                  <td className="px-3 py-2 text-xs text-[var(--color-text-sub)] tabular-nums">
                    {r.granted_at ? fmtDate(r.granted_at) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Pager page={page} totalPages={totalPages} setPage={setPage} hasMore={(data?.total ?? 0) > LIMIT} />
    </>
  );
}

/** 지급 대기 — 자격 진단 + 수동 지급 */
function PendingTab({ page, setPage }: { page: number; setPage: (n: number) => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'referrals', 'pending', page],
    queryFn: () =>
      get<{ total: number; rows: PendingRow[]; page: number; limit: number }>(
        `/admin/referrals/pending?page=${page}&limit=${LIMIT}`,
      ),
    staleTime: 30_000,
  });

  const grantMut = useMutation({
    mutationFn: (id: number) => post(`/admin/referrals/${id}/grant`, {}),
    onSuccess: () => {
      toast.success('수동 지급 완료');
      qc.invalidateQueries({ queryKey: ['admin', 'referrals'] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? '지급 실패'),
  });

  const totalPages = data ? Math.max(1, Math.ceil(data.total / LIMIT)) : 1;
  const qualifiedCount = (data?.rows ?? []).filter((r) => r.qualified).length;

  return (
    <>
      <div className="mb-4 grid grid-cols-3 gap-3">
        <SummaryCard label="지급 대기" value={isLoading ? '-' : `${(data?.total ?? 0).toLocaleString()}건`} />
        <SummaryCard
          label="현재 페이지 자격 충족"
          value={isLoading ? '-' : `${qualifiedCount}건`}
        />
        <SummaryCard
          label="cron 자동 지급 시각"
          value="매일 04:00 KST"
        />
      </div>

      <div className="mb-4 rounded-md border border-[var(--color-border)] bg-[var(--color-info-bg)] p-3 text-xs text-[var(--color-info)]">
        자격: 피추천인(B) 가입 후 <strong>3일 경과</strong> + (게시글 ≥ 1 또는 댓글 ≥ 3, 살아있는 글 기준).
        매일 04:00 KST cron 이 자동 지급해요. 특별 케이스만 수동 지급(super).
      </div>

      {isLoading ? (
        <Skeleton className="h-64" />
      ) : (data?.rows ?? []).length === 0 ? (
        <EmptyMessage>지급 대기 중인 추천이 없어요.</EmptyMessage>
      ) : (
        <div className="space-y-2">
          {data!.rows.map((r) => (
            <div
              key={r.id}
              className={`rounded-xl border p-4 ${
                r.qualified
                  ? 'border-[var(--color-rise)]/40 bg-[var(--color-rise)]/5'
                  : 'border-[var(--color-border)] bg-[var(--color-surface)]'
              }`}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant={r.qualified ? 'rise' : 'default'}>
                      {r.qualified ? '자격 충족' : '미충족'}
                    </Badge>
                    <span className="text-xs text-[var(--color-text-sub)]">
                      신청일 {fmtDate(r.created_at)}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <div>
                      <div className="text-[11px] text-[var(--color-text-sub)]">추천인 (A)</div>
                      <div className="font-semibold">
                        {r.referrer_nickname ?? '-'}
                        {r.referrer_is_deleted && <Badge variant="fall" className="ml-1">탈퇴</Badge>}
                        {r.referrer_is_inactive && <Badge variant="fall" className="ml-1">비활성</Badge>}
                      </div>
                      <code className="text-[10px] text-[var(--color-text-sub)]">{r.referrer_id.slice(0, 8)}…</code>
                      <div className="mt-1 text-xs">+{comma(r.referrer_points)}P 예정</div>
                    </div>
                    <div>
                      <div className="text-[11px] text-[var(--color-text-sub)]">피추천인 (B)</div>
                      <div className="font-semibold">
                        {r.referee_nickname ?? '-'}
                        {r.referee_is_deleted && <Badge variant="fall" className="ml-1">탈퇴</Badge>}
                        {r.referee_is_inactive && <Badge variant="fall" className="ml-1">비활성</Badge>}
                      </div>
                      <code className="text-[10px] text-[var(--color-text-sub)]">{r.referee_id.slice(0, 8)}…</code>
                      <div className="mt-1 text-xs">+{comma(r.referee_points)}P 예정</div>
                    </div>
                  </div>

                  {/* 자격 진단 — 가입 경과 / 활동 */}
                  <div className="mt-3 grid grid-cols-3 gap-2 rounded-md bg-[var(--color-bg)]/40 p-2 text-xs">
                    <Diag label="가입 후 경과" value={`${r.referee_days_elapsed}일`} ok={r.referee_days_elapsed >= 3} />
                    <Diag label="살아있는 게시글" value={`${r.referee_post_count}개`} ok={r.referee_post_count >= 1} />
                    <Diag label="살아있는 댓글" value={`${r.referee_comment_count}개`} ok={r.referee_comment_count >= 3} />
                  </div>

                  {r.pending_reason && (
                    <div className="mt-2 text-xs text-[var(--color-fall)]">
                      미달 사유: {r.pending_reason}
                    </div>
                  )}
                </div>
                <Button
                  size="sm"
                  variant={r.qualified ? 'primary' : 'ghost'}
                  loading={grantMut.isPending && grantMut.variables === r.id}
                  onClick={() => {
                    if (window.confirm(`${r.referrer_nickname ?? '-'} → ${r.referee_nickname ?? '-'}\n수동 지급 처리할까요? (자격 미달 시에도 강제 지급됩니다)`)) {
                      grantMut.mutate(r.id);
                    }
                  }}
                >
                  수동 지급
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Pager page={page} totalPages={totalPages} setPage={setPage} hasMore={(data?.total ?? 0) > LIMIT} />
    </>
  );
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('ko-KR', {
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit',
  });
}

function StatusBadge({ status }: { status: string }) {
  if (status === 'granted') return <Badge variant="rise">완료</Badge>;
  if (status === 'pending') return <Badge variant="editor">대기</Badge>;
  return <Badge variant="default">{status}</Badge>;
}

function Diag({ label, value, ok }: { label: string; value: string; ok: boolean }) {
  return (
    <div>
      <div className="text-[10px] text-[var(--color-text-sub)]">{label}</div>
      <div className={`font-semibold ${ok ? 'text-[var(--color-rise)]' : 'text-[var(--color-text)]'}`}>
        {ok ? '✓ ' : ''}{value}
      </div>
    </div>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <div className="text-xs text-[var(--color-text-sub)]">{label}</div>
      <div className="mt-1 text-xl font-bold tabular-nums">{value}</div>
    </div>
  );
}

function EmptyMessage({ children }: { children: React.ReactNode }) {
  return (
    <p className="rounded-md border border-dashed border-[var(--color-border)] p-8 text-center text-sm text-[var(--color-text-sub)]">
      {children}
    </p>
  );
}

function Pager({
  page,
  totalPages,
  setPage,
  hasMore,
}: {
  page: number;
  totalPages: number;
  setPage: (n: number) => void;
  hasMore: boolean;
}) {
  if (!hasMore) return null;
  return (
    <div className="mt-4 flex items-center justify-center gap-2">
      <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage(Math.max(1, page - 1))}>
        이전
      </Button>
      <span className="text-xs text-[var(--color-text-sub)]">
        {page} / {totalPages}
      </span>
      <Button size="sm" variant="secondary" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
        다음
      </Button>
    </div>
  );
}
